import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {X} from '../../icons';
import {MercadoAdsLogo} from '../../logos/MercadoAdsLogo';
import {MercadoAdsAdvertiserList} from './MercadoAdsAdvertiserList';
import {MercadoAdsErrorView} from './MercadoAdsErrorView';
import {SendConnectionLinkModal} from '../../connection_links/SendConnectionLinkModal';
import {startMercadoAdsAgencyAuth} from './mercadoAdsApi';
import {
    ACTION_SEND_LINK, ERROR_GENERIC, FLOW_AGENCY, consumeConnectResult, historyNoticeText,
} from './mercadoAds';
import {MERCADO_ADS_CHANNEL} from '../channels';

/**
 * La tarjeta de la conexión con Mercado Ads al volver del OAuth, en Conexiones (2.1 y clásico): lo que
 * dejó el callback, leído UNA vez al montar (un F5 no la vuelve a mostrar).
 *
 * - **Bien:** qué cuenta se conectó (o se reconectó), sus anunciantes con país y moneda, que se están
 *   trayendo las campañas y el aviso de los 90 días.
 * - **Mal:** el error con su título, qué hacer y sus acciones, en vez de un toast que se va: el error de
 *   colaborador pide cerrar sesión en Mercado Libre y volver a intentar, o mandarle el link al vendedor.
 *
 * `wrapper(card)` la envuelve sólo cuando hay algo que mostrar (el clásico la mete en una caja de su
 * grilla; sin resultado, no deja un hueco). `start` y `initialResult` son para los tests.
 */
export function MercadoAdsConnectNotice({wrapper = null, start = startMercadoAdsAgencyAuth, initialResult = undefined}) {
    const {t, i18n} = useTranslation();
    const [result, setResult] = useState(() => (initialResult !== undefined ? initialResult : consumeConnectResult(FLOW_AGENCY)));
    const [busyAction, setBusyAction] = useState(null);
    const [sendLinkOpen, setSendLinkOpen] = useState(false);

    if (!result) return null;

    const close = () => setResult(null);
    const onAction = (action) => {
        if (action === ACTION_SEND_LINK) {
            setSendLinkOpen(true);
            return;
        }
        // «Volver a intentar» y «Probar con otra cuenta» vuelven a pedir la autorización.
        setBusyAction(action);
        start().then((outcome) => {
            if (outcome && outcome.ok) return;
            setBusyAction(null);
            setResult({status: (outcome && outcome.code) || ERROR_GENERIC, nickname: null});
        });
    };

    const ok = result.status === 'OK';
    const nicknameArgs = {nickname: result.nickname || '', interpolation: {escapeValue: false}};

    const card = (
        <section className={`ma-result${ok ? ' -ok' : ' -error'}`} data-testid="mercado-ads-result"
                 aria-labelledby="ma-result-title">
            <div className="ma-result__head">
                <MercadoAdsLogo hspace={0} vspace={0} width={28} height={28}/>
                <span className="ma-result__title" id="ma-result-title">
                    {ok
                        ? t(result.relinked ? 'connections.mercado_ads.relinked_ok' : 'connections.mercado_ads.connected_ok', nicknameArgs)
                        : t('channels.mercado_ads.name')}
                </span>
                <button type="button" className="ma-result__close" aria-label={t('connections.mercado_ads.close')} onClick={close}>
                    <X size={16} aria-hidden="true"/>
                </button>
            </div>

            {ok ? (
                <div className="ma-result__body">
                    {result.advertisers && result.advertisers.length > 0
                        ? <MercadoAdsAdvertiserList advertisers={result.advertisers}/>
                        : <span className="ma-result__line">{t('connections.mercado_ads.no_advertisers')}</span>}
                    {!result.relinked && <span className="ma-result__line">{t('connections.mercado_ads.syncing')}</span>}
                    <span className="ma-result__line -notice" data-testid="mercado-ads-history-notice">
                        {historyNoticeText(t, i18n && i18n.language)}
                    </span>
                </div>
            ) : (
                <div className="ma-result__body">
                    <MercadoAdsErrorView code={result.status} nickname={result.nickname} onAction={onAction}
                                         busyAction={busyAction}/>
                </div>
            )}

            <SendConnectionLinkModal show={sendLinkOpen} onHide={() => setSendLinkOpen(false)}
                                     channel={MERCADO_ADS_CHANNEL}/>
        </section>
    );
    return wrapper ? wrapper(card) : card;
}

export default MercadoAdsConnectNotice;
